import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories } from '@/mocks/comics';
import { siteConfig } from '@/mocks/site';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.slug === slug) || categories[0];
  const filteredComics =
    category.slug === 'all'
      ? comics
      : comics.filter((c) =>
          c.category === category.name || c.tags?.some((t) => t === category.name)
        );

  const pageTitle = category.slug === 'all' ? '全部漫画' : `${category.name}漫画`;
  const pageDesc = `韩漫屋${pageTitle}分类 - 共${filteredComics.length}部${category.slug === 'all' ? '韩国' : category.name}漫画，下拉式漫画大全，免费在线阅读。`;

  return (
    <>
      <Navbar />

      <main className="pt-20 md:pt-24">
        <div className="container-custom">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: pageTitle },
            ]}
          />
        </div>

        <section className="py-6 md:py-10">
          <div className="container-custom">
            <div className="mb-8 md:mb-10">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
                {pageTitle}
              </h1>
              <p className="text-sm text-foreground-600">
                共{' '}
                <strong className="text-foreground-950 font-semibold">
                  {filteredComics.length}
                </strong>{' '}
                部{category.slug === 'all' ? '' : category.name}作品
              </p>
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                {filteredComics.map((comic) => (
                  <ComicCard
                    key={comic.id}
                    id={comic.id}
                    title={comic.title}
                    author={comic.author}
                    category={comic.category}
                    rating={comic.rating}
                    views={comic.views}
                    chapters={comic.chapters}
                    status={comic.status}
                    coverImage={comic.coverImage}
                    tags={comic.tags}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类暂无漫画，请浏览其他分类</p>
                <Link
                  to="/category/all"
                  className="inline-block mt-4 px-6 py-2 text-sm font-medium rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
                >
                  浏览全部漫画
                </Link>
              </div>
            )}
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="container-custom">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-8">
              编辑精选
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {comics.filter((c) => c.featured).slice(0, 3).map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group block relative rounded-xl overflow-hidden aspect-[3/4] cursor-pointer"
                  data-product-shop
                >
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    title={`${comic.title} - 韩漫免费看`}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <span className="inline-block text-xs text-accent-400 font-semibold bg-accent-500/30 px-1.5 py-0.5 rounded mb-2">
                      ★ {comic.rating}
                    </span>
                    <h3 className="text-lg font-bold text-background-50 font-heading mb-1">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-background-200/80 line-clamp-2">
                      {comic.description}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'CollectionPage',
              name: `${pageTitle} - ${siteConfig.name}`,
              description: pageDesc,
              url: `${siteConfig.url}/category/${slug}`,
              isPartOf: {
                '@type': 'WebSite',
                name: siteConfig.name,
                url: siteConfig.url,
              },
            }),
          }}
        />
      </main>

      <Footer />
    </>
  );
}